// apps/admin/src/pages/credits/AdminCreditsPage.tsx
//
// Phase 8.2 Wave 2 Task 07 — Credits cross-merchant list page.
//
// Replaces the Phase 3.4b iteration: switches the transport to the
// Phase 8.2 contract (`listFinanceCredits` + `POST /admin/credits/:id/revoke`)
// per CONTEXT.md decisions:
//
//   • Billing tabs (Topup Reviews / Credits / Gateways).
//   • Saved Views per-user filter/sort/column snapshot.
//   • Merchant credit schema is VERSIONED (amount, used
//                remaining, expiresAt, reason, createdBy, version). Each
//                edit creates a new record; the active credit is the
//                latest version where `expires_at > now && remaining > 0`.
//
// Goal: (cross-merchant credit list + revoke flow).
//
// UI contract:
//   • Columns: Amount | Used | Remaining | Expires | Reason | Created By
//             | Version. Status badge + revoke action live in their own
//             column. Row test id = `credits-row-{creditId}`.
//   • Empty state copy: "No credits cross-merchants." (wording).
//   • Filter chips: merchant picker (text), status (active/expired/revoked),
//             expiry range (within N days). Persists via Saved Views chip
//             keyed `admin.credits.list`.
//   • Revoke flow: confirmation modal → reason (>=10 chars) + version → POST
//             `/api/v1/admin/credits/:id/revoke`. On 409 VERSION_CONFLICT we
//             toast the server-provided `user_message`.
//
// vg-binding: CONTEXT:decisions=P8.D-84,P8.D-158,P8.D-204
// vg-binding: PLAN:task-07
// vg-binding: API-CONTRACTS:GET_/api/v1/admin/credits
// vg-binding: API-CONTRACTS:POST_/api/v1/admin/credits/:id/revoke
// vg-binding: INTERFACE-STANDARDS:error-shape

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type JSX,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import {
  Button,
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  useToast,
  PageHeader,
} from '@printway/ui';
import { PageListFooter } from '../../components/datatable/PageListFooter.js';
import { useTranslation } from '../../i18n/useTranslation.js';

import { Money } from '../../components/ui/Money.js';
import { BillingStatusBadge } from '../../components/billing/BillingStatusBadge.js';
import { SavedViewsDropdown } from '../../components/datatable/SavedViewsDropdown.js';
import { financeKeys } from '../../api/queries/financeKeys.js';
import {
  listFinanceCredits,
  unwrapFinanceEnvelope,
} from '../../api/finance.api.js';
import { NETWORK_FALLBACK_MESSAGE } from '../../lib/errorHandler.js';
import type {
  FinanceCreditListFilters,
  FinanceCreditListPayload,
  FinanceCreditRow,
} from '../../types/finance.js';

import {
  CreditsFilters,
  CREDITS_TABLE_KEY,
  type AdminCreditsFilterValue,
} from './CreditsFilters.js';

// ---------------------------------------------------------------------------
// URL <-> filter state.
// ---------------------------------------------------------------------------

const DEFAULT_PAGE_SIZE = 25;

function readFiltersFromUrl(
  sp: URLSearchParams,
): AdminCreditsFilterValue {
  const status = sp.get('status');
  const expiresWithin = sp.get('expires_within_days');
  return {
    q: sp.get('q') ?? '',
    merchantId: sp.get('merchant_id') ?? '',
    status:
      status === 'active' ||
      status === 'used' ||
      status === 'expired' ||
      status === 'revoked' ||
      status === 'all'
        ? status
        : // Default to 'all' so a just-revoked credit stays visible (as a
          // revoked row) instead of vanishing from the previously-default
          // 'active'-only view. debug 2026-07-14.
          'all',
    expiresWithinDays:
      expiresWithin && /^\d+$/.test(expiresWithin)
        ? Math.max(1, Math.min(365, Number(expiresWithin)))
        : '',
  };
}

function readPageFromUrl(sp: URLSearchParams): number {
  const raw = Number(sp.get('page') ?? '1');
  return Number.isFinite(raw) && raw >= 1 ? Math.floor(raw) : 1;
}

function readPageSizeFromUrl(sp: URLSearchParams): number {
  const raw = Number(sp.get('pageSize') ?? String(DEFAULT_PAGE_SIZE));
  if (!Number.isFinite(raw) || raw <= 0) return DEFAULT_PAGE_SIZE;
  return Math.min(100, Math.max(10, Math.floor(raw)));
}

function writeUrlState(
  filters: AdminCreditsFilterValue,
  page: number,
  pageSize: number = DEFAULT_PAGE_SIZE,
): URLSearchParams {
  const sp = new URLSearchParams();
  if (filters.q) sp.set('q', filters.q);
  if (filters.merchantId) sp.set('merchant_id', filters.merchantId);
  if (filters.status && filters.status !== 'all') {
    sp.set('status', filters.status);
  }
  if (filters.expiresWithinDays !== '') {
    sp.set('expires_within_days', String(filters.expiresWithinDays));
  }
  if (page > 1) sp.set('page', String(page));
  if (pageSize !== DEFAULT_PAGE_SIZE) sp.set('pageSize', String(pageSize));
  return sp;
}

// ---------------------------------------------------------------------------
// API params projection.
// ---------------------------------------------------------------------------

function toApiFilters(
  filters: AdminCreditsFilterValue,
  page: number,
  pageSize: number,
): FinanceCreditListFilters {
  const out: FinanceCreditListFilters = {
    page,
    limit: pageSize,
  };
  if (filters.merchantId) out.merchant_id = filters.merchantId;
  if (filters.status) out.status = filters.status;
  if (filters.expiresWithinDays !== '') {
    out.expires_within_days = Number(filters.expiresWithinDays);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Revoke mutation — local axios POST so we keep the user_message envelope.
// ---------------------------------------------------------------------------

interface RevokeArgs {
  creditId: string;
  reason: string;
  expectedVersion: number;
}

async function revokeCreditRequest(args: RevokeArgs): Promise<void> {
  try {
    const res = await axios.post(
      `/api/v1/admin/credits/${encodeURIComponent(args.creditId)}/revoke`,
      {
        reason: args.reason,
        expected_version: args.expectedVersion,
      },
      { withCredentials: true },
    );
    const unwrap = unwrapFinanceEnvelope<unknown, unknown>(res.data);
    if (!unwrap.ok) {
      throw new RevokeError(unwrap.code, unwrap.userMessage);
    }
  } catch (err) {
    if (axios.isAxiosError(err) && err.response) {
      const unwrap = unwrapFinanceEnvelope<unknown, unknown>(
        err.response.data,
      );
      if (!unwrap.ok) {
        throw new RevokeError(unwrap.code, unwrap.userMessage);
      }
    }
    if (err instanceof RevokeError) throw err;
    throw new RevokeError('NETWORK_ERROR', NETWORK_FALLBACK_MESSAGE);
  }
}

class RevokeError extends Error {
  code: string;
  userMessage: string;
  constructor(code: string, userMessage: string) {
    super(userMessage);
    this.name = 'RevokeError';
    this.code = code;
    this.userMessage = userMessage;
  }
}

// ---------------------------------------------------------------------------
// Display helpers.
// ---------------------------------------------------------------------------

// Status badges delegated to BillingStatusBadge so they honour the canonical
// @printway/ui Badge tokens (no border, 100/700-800 soft tint, font-semibold).

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString();
}

// ---------------------------------------------------------------------------
// Page component.
// ---------------------------------------------------------------------------

export function AdminCreditsPage(): JSX.Element {
  const { t } = useTranslation('finance');
  const { toast } = useToast();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo(
    () => readFiltersFromUrl(searchParams),
    [searchParams],
  );
  const page = useMemo(() => readPageFromUrl(searchParams), [searchParams]);
  const pageSize = useMemo(
    () => readPageSizeFromUrl(searchParams),
    [searchParams],
  );
  const apiFilters = useMemo(
    () => toApiFilters(filters, page, pageSize),
    [filters, page, pageSize],
  );

  const query = useQuery<FinanceCreditListPayload>({
    queryKey: financeKeys.credits.list(apiFilters),
    queryFn: () => listFinanceCredits(apiFilters),
    placeholderData: (prev) => prev,
  });

  // Surface fetch errors via toast — INTERFACE-STANDARDS message-priority.
  useEffect(() => {
    if (query.error instanceof Error) {
      toast({
        variant: 'error',
        title: t('credit.errors.loadFailed'),
        description: query.error.message || NETWORK_FALLBACK_MESSAGE,
      });
    }
  }, [query.error, toast, t]);

  // Revoke mutation
  const revoke = useMutation({
    mutationFn: revokeCreditRequest,
    onSuccess: () => {
      toast({
        variant: 'success',
        title: t('credit.revoke.successTitle'),
        description: t('credit.revoke.successDesc'),
      });
      void qc.invalidateQueries({ queryKey: financeKeys.credits.all });
    },
  });

  // Local state — revoke dialog target row + form.
  const [revokeTarget, setRevokeTarget] = useState<FinanceCreditRow | null>(
    null,
  );
  const [revokeReason, setRevokeReason] = useState('');
  const [revokeError, setRevokeError] = useState<string | null>(null);

  const handleFilterChange = useCallback(
    (next: AdminCreditsFilterValue) => {
      setSearchParams(writeUrlState(next, 1, pageSize), { replace: true });
    },
    [pageSize, setSearchParams],
  );

  const handlePageChange = useCallback(
    (nextPage: number) => {
      if (nextPage < 1) return;
      setSearchParams(writeUrlState(filters, nextPage, pageSize), {
        replace: true,
      });
    },
    [filters, pageSize, setSearchParams],
  );

  const handlePageSizeChange = useCallback(
    (nextSize: number) => {
      // Phase 8.2 audit 2026-05-23 — pageSize change resets to page 1.
      setSearchParams(writeUrlState(filters, 1, nextSize), { replace: true });
    },
    [filters, setSearchParams],
  );

  const handleApplySavedView = useCallback(
    (view: { state: { filters: Record<string, string> } }) => {
      const f = view.state.filters ?? {};
      const expiresRaw = f.expires_within_days;
      const status = f.status;
      const next: AdminCreditsFilterValue = {
        q: f.q ?? '',
        merchantId: f.merchant_id ?? '',
        status:
          status === 'active' ||
          status === 'used' ||
          status === 'expired' ||
          status === 'revoked' ||
          status === 'all'
            ? status
            : 'all',
        expiresWithinDays:
          expiresRaw && /^\d+$/.test(expiresRaw)
            ? Math.max(1, Math.min(365, Number(expiresRaw)))
            : '',
      };
      handleFilterChange(next);
    },
    [handleFilterChange],
  );

  const openRevoke = useCallback((row: FinanceCreditRow) => {
    setRevokeTarget(row);
    setRevokeReason('');
    setRevokeError(null);
  }, []);

  const closeRevoke = useCallback(() => {
    if (revoke.isPending) return;
    setRevokeTarget(null);
    setRevokeReason('');
    setRevokeError(null);
  }, [revoke.isPending]);

  const handleConfirmRevoke = useCallback(async () => {
    if (!revokeTarget) return;
    const trimmed = revokeReason.trim();
    if (trimmed.length < 10 || trimmed.length > 500) {
      setRevokeError(t('credit.revoke.reasonRequired'));
      return;
    }
    setRevokeError(null);
    try {
      await revoke.mutateAsync({
        creditId: revokeTarget.id,
        reason: trimmed,
        expectedVersion: revokeTarget.version,
      });
      setRevokeTarget(null);
      setRevokeReason('');
    } catch (err) {
      if (err instanceof RevokeError) {
        setRevokeError(err.userMessage);
        toast({
          variant: 'error',
          title: t('credit.revoke.failedTitle'),
          description: err.userMessage,
        });
      } else {
        const msg =
          err instanceof Error ? err.message : NETWORK_FALLBACK_MESSAGE;
        setRevokeError(msg);
      }
    }
  }, [revokeTarget, revokeReason, revoke, toast, t]);

  const rows = query.data?.rows ?? [];
  const total = query.data?.meta.total ?? 0;
  const limit = query.data?.meta.limit ?? DEFAULT_PAGE_SIZE;
  const totalPages = Math.max(1, Math.ceil(total / Math.max(limit, 1)));
  const isInitialLoading = query.isLoading && rows.length === 0;
  const isEmpty =
    !isInitialLoading && !query.isFetching && rows.length === 0;

  // Saved-view snapshot — record the same wire-keys we read from URL so the
  // restore round-trip is symmetric.
  const savedViewState = useMemo(
    () => ({
      filters: {
        q: filters.q,
        merchant_id: filters.merchantId,
        status: filters.status,
        expires_within_days:
          filters.expiresWithinDays === ''
            ? ''
            : String(filters.expiresWithinDays),
      },
      sort: [],
      columnVisibility: {},
      pageSize,
    }),
    [filters, pageSize],
  );

  return (
    <main className="flex flex-col gap-4 p-6">
      <PageHeader
        title={t('credit.title')}
        subtitle={t('credit.listSubtitle')}
        actions={
          <SavedViewsDropdown
            tableKey={CREDITS_TABLE_KEY}
            currentState={savedViewState}
            onApply={handleApplySavedView}
          />
        }
      />

      <CreditsFilters value={filters} onChange={handleFilterChange} />

      <section className="rounded-lg border border-pw-border bg-pw-bg">
        <div className="overflow-x-auto">
          <table
            className="min-w-full divide-y divide-pw-border text-sm"
            data-testid="admin-credits-table"
          >
            <thead className="bg-pw-surface text-xs uppercase tracking-wide text-pw-text-muted">
              <tr>
                <th scope="col" className="px-3 py-2 text-left font-medium">
                  {t('credit.columns.merchant')}
                </th>
                <th scope="col" className="px-3 py-2 text-right font-medium">
                  {t('credit.columns.amount')}
                </th>
                <th scope="col" className="px-3 py-2 text-right font-medium">
                  {t('credit.columns.used')}
                </th>
                <th scope="col" className="px-3 py-2 text-right font-medium">
                  {t('credit.columns.remaining')}
                </th>
                <th scope="col" className="px-3 py-2 text-left font-medium">
                  {t('credit.columns.expiresAt')}
                </th>
                <th scope="col" className="px-3 py-2 text-left font-medium">
                  {t('credit.columns.reason')}
                </th>
                <th scope="col" className="px-3 py-2 text-left font-medium">
                  {t('credit.columns.createdBy')}
                </th>
                <th scope="col" className="px-3 py-2 text-right font-medium">
                  {t('credit.columns.version')}
                </th>
                <th scope="col" className="px-3 py-2 text-left font-medium">
                  {t('credit.columns.status')}
                </th>
                <th scope="col" className="px-3 py-2 text-right font-medium">
                  {t('credit.columns.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-pw-border">
              {isInitialLoading ? (
                <tr>
                  <td
                    colSpan={10}
                    className="px-3 py-8 text-center text-pw-text-muted"
                  >
                    {t('credit.loading')}
                  </td>
                </tr>
              ) : isEmpty ? (
                <tr>
                  <td
                    colSpan={10}
                    className="px-3 py-12 text-center"
                    data-testid="admin-credits-empty-state"
                  >
                    <div className="mx-auto flex max-w-md flex-col items-center gap-2 text-pw-text-muted">
                      <span className="text-base font-medium text-pw-text">
                        {t('credit.empty.title')}
                      </span>
                      <span className="text-sm">
                        {t('credit.empty.bodyBefore')}{' '}
                        <strong>{t('credit.empty.tabName')}</strong>{' '}
                        {t('credit.empty.bodyAfter')}
                      </span>
                    </div>
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr
                    key={row.id}
                    className="hover:bg-pw-surface"
                    data-testid={`credits-row-${row.id}`}
                  >
                    <td className="px-3 py-2 align-top text-pw-text">
                      <span className="block font-medium">
                        {row.merchant_name || '—'}
                      </span>
                      <span
                        className="block font-mono text-xs text-pw-text-muted"
                        title={row.merchant_id}
                      >
                        {row.merchant_id}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right align-top">
                      <Money usdCents={row.amount_cents} align="right" showConverted={false} />
                    </td>
                    <td className="px-3 py-2 text-right align-top">
                      <Money usdCents={row.used_cents} align="right" showConverted={false} />
                    </td>
                    <td className="px-3 py-2 text-right align-top">
                      <Money usdCents={row.remaining_cents} align="right" showConverted={false} />
                    </td>
                    <td className="px-3 py-2 align-top text-pw-text-muted">
                      {formatDate(row.expires_at)}
                    </td>
                    <td className="px-3 py-2 align-top text-pw-text" title={row.reason}>
                      <span className="line-clamp-2">{row.reason || '—'}</span>
                    </td>
                    <td className="px-3 py-2 align-top text-pw-text-muted">
                      {row.created_by}
                    </td>
                    <td className="px-3 py-2 text-right align-top text-pw-text-muted">
                      v{row.version}
                    </td>
                    <td className="px-3 py-2 align-top">
                      <BillingStatusBadge status={row.status} />
                    </td>
                    <td className="px-3 py-2 text-right align-top">
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => openRevoke(row)}
                        disabled={row.status === 'revoked'}
                        data-testid={`credits-revoke-btn-${row.id}`}
                      >
                        {t('credit.actions.revoke')}
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination — canonical /stores footer (Rows-per-page + Total +
            numbered) per audit 2026-05-23 Cluster A. Replaces the bespoke
            Prev/Next pair so the Rows selector is reachable. */}
        {!isInitialLoading && rows.length > 0 ? (
          <div className="border-t border-pw-border px-3 py-2">
            <PageListFooter
              page={page}
              pageSize={pageSize}
              total={total}
              totalPages={totalPages}
              loading={query.isFetching}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              testId="admin-credits-footer"
            />
          </div>
        ) : null}
      </section>

      {/* Revoke confirmation modal. */}
      {revokeTarget ? (
        <Dialog
          open={!!revokeTarget}
          onOpenChange={(o) => {
            if (!o) closeRevoke();
          }}
        >
          <DialogHeader>
            <DialogTitle>{t('credit.revoke.dialogTitle')}</DialogTitle>
          </DialogHeader>
          <div className="mt-3 space-y-4 text-sm">
            <div
              role="alert"
              className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/40 p-3 text-amber-900"
              data-testid="credits-revoke-warning"
            >
              <p className="font-medium">
                {t('credit.revoke.merchantLabel')}{' '}
                <span className="font-mono">{revokeTarget.merchant_name}</span>
              </p>
              <p className="mt-1 text-xs">
                {t('credit.revoke.warning', { version: revokeTarget.version })}
              </p>
            </div>

            <div>
              <label
                htmlFor="credits-revoke-reason"
                className="mb-1 block text-sm font-medium"
              >
                {t('credit.revoke.reasonLabel')}
              </label>
              <textarea
                id="credits-revoke-reason"
                data-testid="credits-revoke-reason-input"
                className="block h-24 w-full rounded-md border border-pw-border bg-pw-bg p-2 text-sm focus:border-pw-primary focus:outline-none focus:ring-2 focus:ring-pw-primary/20"
                value={revokeReason}
                onChange={(e) => setRevokeReason(e.target.value)}
                maxLength={500}
                disabled={revoke.isPending}
              />
              <div className="mt-1 flex items-center justify-between text-xs text-pw-text-muted">
                <span>
                  {revokeReason.trim().length < 10
                    ? t('credit.revoke.charsNeeded', {
                        count: 10 - revokeReason.trim().length,
                      })
                    : t('credit.revoke.lengthOk')}
                </span>
                <span>{revokeReason.length} / 500</span>
              </div>
            </div>

            {revokeError ? (
              <p
                className="text-sm text-red-600 dark:text-red-300"
                data-testid="credits-revoke-error"
              >
                {revokeError}
              </p>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={closeRevoke}
              disabled={revoke.isPending}
            >
              {t('credit.revoke.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={() => void handleConfirmRevoke()}
              disabled={revoke.isPending}
              data-testid="credits-revoke-confirm"
            >
              {revoke.isPending
                ? t('credit.revoke.submitting')
                : t('credit.revoke.submit')}
            </Button>
          </DialogFooter>
        </Dialog>
      ) : null}
    </main>
  );
}

export default AdminCreditsPage;
